/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import type { LeaseState as GeneratedLeaseState } from '@/services/requests/generated/types.gen';

/**
 * The seven Lease states, in graph order.
 *
 * The type comes from the generated client, so this list cannot name a
 * state the API does not serve -- a wrong value fails the typecheck
 * rather than surviving until someone notices in production. It was a
 * hand-written copy until the spec declared the enum.
 */
export type LeaseState = GeneratedLeaseState;

export const LEASE_STATES: readonly LeaseState[] = [
  'draft',
  'active',
  'suspended',
  'extended',
  'terminated',
  'closed',
  'cancelled',
] as const;

/** The lifecycle actions the transitions route accepts. */
export const LEASE_ACTIONS = [
  'activate',
  'suspend',
  'resume',
  'rescind',
  'close',
  'cancel',
] as const;

export type LeaseAction = (typeof LEASE_ACTIONS)[number];

/**
 * Which actions the UI may offer from a given state.
 *
 * Derived from the backend's transition graph intersected with the
 * actions the route actually dispatches. `cancel` requires a reason —
 * the backend answers 422 without one.
 *
 * One declared edge is still unreachable, deliberately:
 * `active -> extended`. A Lease has no contract term at all (no start,
 * no end, only `activated_at`), so "extend" has nothing to extend and
 * no new date to write, and the backend already treats `extended` as
 * running — the state would change no behaviour. It stays reserved
 * because prorrogação is a real fact of the business and returns with
 * contract vigência.
 */
const ACTIONS_BY_STATE: Record<LeaseState, LeaseAction[]> = {
  draft: ['activate', 'cancel'],
  active: ['suspend', 'rescind'],
  suspended: ['resume', 'rescind'],
  extended: ['suspend', 'rescind'],
  terminated: ['close'],
  closed: [],
  cancelled: [],
};

/**
 * Actions available from a state, in graph order.
 *
 * @param state - The lease's current state.
 * @returns The offerable lifecycle actions (empty for terminal states).
 */
export function getActionsForState(state: string): LeaseAction[] {
  return ACTIONS_BY_STATE[state as LeaseState] ?? [];
}

/** Accent tones per state, matching the temperature-badge approach [Fase 2]. */
const NEUTRAL = { bg: 'var(--surface-tertiary)', color: 'var(--text-secondary)' };

/**
 * One theme status family per state, by how much it asks of the broker:
 * waiting to be signed (info), running (success), paused (warning), ended
 * early (error), and finished either way (neutral). The colours used to be
 * per-state brand accents mixed on the spot, which no other module shares.
 */
export const LEASE_STATE_STYLES: Record<LeaseState, { bg: string; color: string }> = {
  draft: { bg: 'var(--status-info-bg)', color: 'var(--status-info-text)' },
  active: { bg: 'var(--status-success-bg)', color: 'var(--status-success-text)' },
  suspended: { bg: 'var(--status-warning-bg)', color: 'var(--status-warning-text)' },
  extended: { bg: 'var(--status-success-bg)', color: 'var(--status-success-text)' },
  terminated: { bg: 'var(--status-error-bg)', color: 'var(--status-error-text)' },
  closed: NEUTRAL,
  cancelled: NEUTRAL,
};

/**
 * Resolve the display label for a lease state.
 *
 * @returns A translator from state value to localized label.
 */
export function useLeaseStateLabel(): (state: string) => string {
  const { t } = useTranslation('features/leases');
  return (state) => t(`states.${state}`, { defaultValue: state });
}

/**
 * Resolve the display label for a lifecycle action -- phrased as the verb
 * the broker performs, never as the target state.
 *
 * @returns A translator from action value to localized label.
 */
export function useLeaseActionLabel(): (action: string) => string {
  const { t } = useTranslation('features/leases');
  return (action) => t(`actions.${action}`, { defaultValue: action });
}

/**
 * The five ids the backend seeds every agency with. i18n translates these by
 * id (`documents.categories.<id>`); a category the agency added has no key
 * and shows its own stored label instead.
 */
export const BUILT_IN_DOCUMENT_CATEGORY_IDS = new Set([
  'contract', 'inspection', 'warranty', 'termination', 'other',
]);

/** Document categories for a lease's own papers [Plano 4]. */
export const LEASE_DOCUMENT_CATEGORIES = [
  'contract',
  'inspection',
  'warranty',
  'termination',
  'other',
] as const;
